/** Small display helpers. Spanish, because the UI is. */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "hace 3 s" / "hace 2 min". Deliberately coarse past the first minute: an
 * inspector is read at a glance, and a request's exact second is in the detail
 * panel for when it matters.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const elapsed = now - new Date(iso).getTime();

  if (Number.isNaN(elapsed)) return '';
  if (elapsed < 0) return 'ahora';
  if (elapsed < 10_000) return 'ahora';
  if (elapsed < MINUTE) return `hace ${Math.floor(elapsed / 1000)} s`;
  if (elapsed < HOUR) return `hace ${Math.floor(elapsed / MINUTE)} min`;
  if (elapsed < DAY) return `hace ${Math.floor(elapsed / HOUR)} h`;
  return `hace ${Math.floor(elapsed / DAY)} d`;
}

/** "en 6 días" / "en 3 h" — how long an endpoint has left. */
export function timeUntil(iso: string, now: number = Date.now()): string {
  const remaining = new Date(iso).getTime() - now;

  if (Number.isNaN(remaining)) return '';
  if (remaining <= 0) return 'caducado';
  if (remaining < HOUR) return `en ${Math.max(1, Math.floor(remaining / MINUTE))} min`;
  if (remaining < DAY) return `en ${Math.floor(remaining / HOUR)} h`;

  const days = Math.floor(remaining / DAY);
  return `en ${days} ${days === 1 ? 'día' : 'días'}`;
}

export function expiresSoon(iso: string, now: number = Date.now()): boolean {
  const remaining = new Date(iso).getTime() - now;
  return remaining > 0 && remaining < DAY;
}

export function hasExpired(iso: string, now: number = Date.now()): boolean {
  return new Date(iso).getTime() <= now;
}

export function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'medium' });
}

/** Tailwind classes for a status code, by class of outcome. */
export function statusTone(status: number): string {
  if (status >= 500) return 'text-rose-300 bg-rose-500/10 ring-rose-500/30';
  if (status >= 400) return 'text-amber-300 bg-amber-500/10 ring-amber-500/30';
  if (status >= 300) return 'text-sky-300 bg-sky-500/10 ring-sky-500/30';
  return 'text-emerald-300 bg-emerald-500/10 ring-emerald-500/30';
}

/**
 * Colour per verb, so the list is scannable without reading. Read-only verbs
 * stay muted and the destructive ones stand out.
 */
export function methodTone(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'text-sky-300 bg-sky-500/10';
    case 'POST':
      return 'text-emerald-300 bg-emerald-500/10';
    case 'PUT':
    case 'PATCH':
      return 'text-amber-300 bg-amber-500/10';
    case 'DELETE':
      return 'text-rose-300 bg-rose-500/10';
    default:
      return 'text-slate-300 bg-slate-500/10';
  }
}
